import React from "react";
import MiniBarChart from "./MiniBarchart";
import { env } from "../../config";

const HDR_BG   = "#374151";
const HDR_ALT  = "#4b5563";
const ROW_EVEN = "#ffffff";
const ROW_ODD  = "#f8fafc";
const ROW_HIDDEN = "#f1f5f9";

function isNumeric(v) {
    return v !== null && v !== undefined && v !== "" && !isNaN(Number(v));
}

function columnAverages(participants, colCount) {
    return Array.from({ length: colCount }, (_, ci) => {
        let sum = 0, count = 0;
        participants.forEach(p => {
            const v = p.values[ci];
            if (isNumeric(v)) { sum += Number(v); count++; }
        });
        return count > 0 ? sum / count : null;
    });
}

function NumericCell({ raw, avg, maxValue, CELL }) {
    const num = Number(raw);
    return (
        <td key="num" style={{ ...CELL, padding: "4px 6px", verticalAlign: "bottom" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                <MiniBarChart value={num} avg={avg} max={maxValue} />
                <span style={{
                    fontSize: "10px",
                    fontWeight: "700",
                    color: "#1e293b",
                    fontFamily: "monospace",
                    lineHeight: 1
                }}>{num}</span>
            </div>
        </td>
    );
}

function StringCell({ raw, CELL }) {
    const str = String(raw);
    return (
        <td key="str" style={{ ...CELL, padding: "5px 6px" }}>
            <span title={str} style={{
                display: "inline-block",
                background: "#e0f2fe",
                border: "1px solid #bae6fd",
                borderRadius: "4px",
                padding: "2px 6px",
                fontSize: "10px",
                color: "#0369a1",
                fontFamily: "monospace",
                fontWeight: "600",
                maxWidth: "88px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                verticalAlign: "middle"
            }}>
                {str}
            </span>
        </td>
    );
}

function MissingCell({ CELL }) {
    return (
        <td key="miss" style={CELL}>
            <span style={{ fontWeight: "700", fontSize: "15px", color: "#cbd5e1" }}>×</span>
        </td>
    );
}

function ValueCells({ participant, colCount, averages, maxValue, CELL }) {
    return Array.from({ length: colCount }).map((_, ci) => {
        const raw = participant.values[ci];
        if (raw === undefined || raw === null) return <MissingCell key={ci} CELL={CELL} />;
        if (isNumeric(raw))  return <NumericCell  key={ci} raw={raw} avg={averages[ci]} maxValue={maxValue} CELL={CELL} />;
        return <StringCell key={ci} raw={raw} CELL={CELL} />;
    });
}

export default function OutlierTable({ outlierData, jwtToken, handleUpdate, full = true }) {
    if (!outlierData || Object.keys(outlierData).length === 0) {
        return (
            <div style={{
                padding: "16px",
                background: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: "6px",
                textAlign: "center",
                fontSize: "13px",
                color: "#92400e"
            }}>
                <strong>No data available.</strong>
            </div>
        );
    }

    const hideParticipant = (token, id) => {
        fetch(env.BACKEND_URL + `/participants/${id}`, {
            method: "DELETE",
            headers: { "Authorization": "Bearer " + token }
        }).then(() => handleUpdate());
    };

    const unhideParticipant = (token, id) => {
        fetch(env.BACKEND_URL + `/participants/${id}`, {
            method: "POST",
            headers: { "Authorization": "Bearer " + token }
        }).then(() => handleUpdate());
    };

    const allParticipants    = Object.entries(outlierData).map(([pid, data]) => ({
        participant: pid,
        hidden: data.hidden,
        values: data.values
    }));
    const participants       = allParticipants.filter(p => !p.hidden);
    const hiddenParticipants = allParticipants.filter(p =>  p.hidden);
    const colCount           = Math.max(...allParticipants.map(p => p.values.length), 0);

    const averages = columnAverages(participants, colCount);
    const numericValues = participants.flatMap(p =>
        p.values.filter(v => isNumeric(v)).map(v => Number(v))
    );
    const maxValue = Math.max(...numericValues, ...averages.filter(v => v !== null), 0);

    const HDR = {
        background: HDR_BG,
        color: "white",
        padding: "6px 10px",
        fontWeight: "600",
        fontSize: "11px",
        textAlign: "center",
        whiteSpace: "nowrap",
        borderRight: `1px solid ${HDR_ALT}`,
        borderBottom: "none",
        position: "sticky",
        top: 0,
        zIndex: 1
    };
    const CELL = {
        padding: "5px 8px",
        fontSize: "11px",
        textAlign: "center",
        verticalAlign: "middle",
        borderRight: "1px solid #e5e7eb",
        borderBottom: "1px solid #e5e7eb"
    };
    const ACTION_CELL = { ...CELL, width: "72px" };
    const ID_CELL     = { ...CELL, width: "110px", fontWeight: "700", fontFamily: "monospace", color: "#1e293b" };
    const ID_CELL_HID = { ...ID_CELL, color: "#94a3b8" };

    const tableHead = (
        <thead>
            <tr>
                {full && <th style={{ ...HDR, width: "72px" }}>Action</th>}
                <th style={{ ...HDR, width: "110px", textAlign: "center", paddingLeft: "14px" }}>
                    Participant ID
                </th>
                {Array.from({ length: colCount }).map((_, ci) => (
                    <th key={ci} style={{ ...HDR, background: ci % 2 === 0 ? HDR_BG : HDR_ALT }}>
                        Val {ci + 1}
                    </th>
                ))}
            </tr>
        </thead>
    );

    return (
        <div>
            <div style={{ width: "100%", overflowX: "auto", overflowY: "auto", maxHeight: "680px" }}>
                <table style={{ borderCollapse: "collapse", width: "100%", whiteSpace: "nowrap" }}>
                    {tableHead}
                    <tbody>
                        {participants.map((p, rowIndex) => (
                            <tr key={rowIndex} style={{ background: rowIndex % 2 === 0 ? ROW_EVEN : ROW_ODD }}>
                                {full && (
                                    <td style={ACTION_CELL}>
                                        <button
                                            className="btn btn-xs btn-default"
                                            style={{
                                                fontSize: "10px",
                                                padding: "2px 8px",
                                                fontWeight: "600",
                                                border: "1px solid #d1d5db",
                                                borderRadius: "4px",
                                                color: "#374151",
                                                background: "white"
                                            }}
                                            onClick={() => hideParticipant(jwtToken, p.participant)}
                                        >Hide</button>
                                    </td>
                                )}
                                <td style={{ ...ID_CELL, textAlign: "center", paddingLeft: "14px" }}>
                                    {p.participant}
                                </td>
                                <ValueCells
                                    participant={p}
                                    colCount={colCount}
                                    averages={averages}
                                    maxValue={maxValue}
                                    CELL={CELL}
                                />
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {hiddenParticipants.length > 0 && (
                <>
                    <div style={{
                        background: "#f1f5f9",
                        borderTop: "2px dashed #94a3b8",
                        borderBottom: "1px solid #e2e8f0",
                        padding: "5px 14px",
                        fontSize: "11px",
                        color: "#64748b",
                        fontStyle: "italic",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                    }}>
                        <span>🚫</span>
                        <span>Hidden participants: excluded from analysis. Click <strong>Unhide</strong> to restore.</span>
                    </div>
                    <div style={{ width: "100%", overflowX: "auto", overflowY: "auto", maxHeight: "145px" }}>
                        <table style={{ borderCollapse: "collapse", width: "100%", whiteSpace: "nowrap" }}>
                            {tableHead}
                            <tbody>
                                {hiddenParticipants.map((p, rowIndex) => (
                                    <tr key={"h-" + rowIndex} style={{ background: ROW_HIDDEN, opacity: 0.7 }}>
                                        {full && (
                                            <td style={ACTION_CELL}>
                                                <button
                                                    className="btn btn-xs btn-primary"
                                                    style={{
                                                        fontSize: "10px",
                                                        padding: "2px 8px",
                                                        fontWeight: "600",
                                                        borderRadius: "4px"
                                                    }}
                                                    onClick={() => unhideParticipant(jwtToken, p.participant)}
                                                >Unhide</button>
                                            </td>
                                        )}
                                        <td style={{ ...ID_CELL_HID, textAlign: "left", paddingLeft: "14px" }}>
                                            {p.participant}
                                        </td>
                                        <ValueCells
                                            participant={p}
                                            colCount={colCount}
                                            averages={averages}
                                            maxValue={maxValue}
                                            CELL={CELL}
                                        />
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <div style={{
                padding: "6px 14px",
                background: "#f9fafb",
                borderTop: "1px solid #e5e7eb",
                fontSize: "11px",
                color: "#6b7280",
                display: "flex",
                gap: "16px",
                flexWrap: "wrap",
                alignItems: "center"
            }}>
                <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <svg width="14" height="14"><rect x="2" y="3" width="10" height="8" fill="#3b82f6" rx="1" opacity="0.85"/></svg>
                    Bar height = participant value
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <svg width="14" height="14"><line x1="1" y1="7" x2="13" y2="7" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round"/></svg>
                    Red line = column average
                </span>
                <span>× = no value recorded</span>
                <span>Scroll horizontally to view all columns</span>
            </div>
        </div>
    );
}
